import { AREA_TYPE } from '@/constants/area-constants';
import { PaginationModel } from '@/models/Pagination';
import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import { AccessAreaCard } from '../../../components/accessArea/AccessAreaCard';
import AccessAreaDetails from '../../../components/accessArea/AccessAreaDetails';
import AssignModalTwo from '../../../components/assignWizard/AssignModal';
import AssignedTab from '../../../components/assignWizard/AssignedTab';
import { Button } from '../../../components/button/Button';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { ACCESS_TYPES } from '../../../constants/access-constants';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { AccessArea as AccessAreaModel } from '../../../models/AccessArea';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import AccessGroupColumns from '../../../models/ColumnDefinitions/AccessGroupColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { TabSummary } from '../../../models/TabSummary';
import AccessAreaSearch from '../../../models/search/AccessAreaSearch';
import AccessGroupSearch from '../../../models/search/AccessGroupSearch';
import AccessTimePatternSearch from '../../../models/search/AccessTimePatternSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { hideColumns } from '../../../models/utilities/columnHelper';
import { getAccessAreaById, getAccessAreaNamesWithPagination, storeAccessArea } from '../../../services/accessAreaService';
import { assignUnassignAccessGroups, getAccessGroupsNamesWithPagination, getAllAccessGroups } from '../../../services/accessGroupService';
import { getAccessTimePatternNamesWithPagination } from '../../../services/accessTimePatternService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { getDayNames } from '../../../utilities/DateUtils';
import { appendFilter } from '../../../utilities/filters';
import { Filter } from '@/models/Filter';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

interface AccessAreaProps {
    areaType: AREA_TYPE;
}

const AccessArea: FC<AccessAreaProps> = ({ areaType }) => {
    const initialItem: AccessAreaModel = {
        id: 0,
        name: '',
        site: '',
        siteId: 0,
        description: '',
        doorList: [],
        doors: 0,
        doorIds: [],
        default: undefined,
    }
    const { translate } = useLocale();
    const isAccessArea = areaType === ACCESS_TYPES.ACCESS_AREA;
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(isAccessArea ? TranslationKeys.accessGroups : TranslationKeys.floorGroups), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<AccessAreaModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [showManageModal, setShowManageModal] = useState(false);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const allItemsPath = '/access-control/enrolment/access-areas';
    const itemPath = isAccessArea ? '/access-control/enrolment/access-area/' : '/access-control/enrolment/lift-area/';
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find((item) => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNamesWithPagination,
            searchAccessor: AccessTimePatternSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ];

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const loadSiteData = async () => {
        setBusy(true)
        try {
            if (itemId) {
                const accessArea = await getAccessAreaById(itemId);

                setSelectedItem({ label: accessArea.name, value: accessArea.id.toString(), isDefault: accessArea.isDefault });

                setCurrentItem(accessArea)
                setBreadcrumb([
                    { value: '/access-control/enrolment', displayName: translate.byKey(TranslationKeys.enrolments) },
                    { value: allItemsPath, displayName: translate.byKey(isAccessArea ? TranslationKeys.accessAreas : TranslationKeys.liftAreas) },
                    { value: itemPath + `${itemId}`, displayName: `${accessArea?.name ?? ''}` }
                ]);
                setTabs(updateTabBadgeTotal(1, (isAccessArea ? accessArea.accessGroups : accessArea.floorGroups) ?? 0, initialTabs));
                return;
            }

            setCurrentItem(initialItem);
            setTabs([initialTabs[2]]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        } finally {
            setBusy(false)
        }

    }
    
    const tabContentList: ContentWrapper[] = [
        {
            content: <AccessAreaDetails accessArea={currentItem} />
        },
        {
            content: <AssignedTab
                key={`assignedGroupsForArea${itemId}`}
                column={AccessGroupColumns}
                searchColumn={AccessGroupSearch}
                dataMethod={(listParameters?: ListQuery) => getAllAccessGroups(areaType, translate, {...listParameters, filters: appendFilter(itemId, 'Area', 'area.id', listParameters?.filters ?? []) })}
                id={itemId}
                lookup={lookup}
                title={translate.byKey(isAccessArea ? TranslationKeys.assignedAccessGroups : TranslationKeys.assignedFloorGroups)}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowManageModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ];

    /**
    * Fetch all topics for the topic list component
    * @returns
    */
    const topic: Topic = {
        accessor: 'areaName',
        allItemsPath,
        itemPath,
        title: isAccessArea ? translate.byKey(TranslationKeys.accessAreas) : translate.byKey(TranslationKeys.liftAreas),
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveAccessArea = async (values: any) => {
        const accessArea: AccessAreaModel = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : site.value,
            id: +itemId,
            site: itemId ? values.site : site.label,
            description: values.description,
            ...(areaType !== null && { areaType: areaType }),
        };

        const createNewAccessArea = itemId ? +itemId < 0 : true;

        storeAccessArea(accessArea, createNewAccessArea).then((value: any) => {
            setItemId(value.id);
            history.push(`${itemPath}${value.id}`);
            toast.success(translate.byKey(TranslationKeys.accessAreaSaved));
        }).catch(failure => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
        }).finally(() => {
            setBusy(false);
        });
    }

    useEffect(() => {
        loadSiteData();
    }, [itemId]);

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    const getAssignedAccessGroups = async (
        setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, 
        setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, 
        setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
        setOriginalSelectedRows: React.Dispatch<React.SetStateAction<string[]>>,  
        setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>,
        setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {

        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
            do {
                const accessGroups = (await getAccessGroupsNamesWithPagination(areaType, { filters: itemId ? appendFilter(itemId, 'Area', 'area.id', []) : [], pageSize: 100, goToPage: pageNumber }));
                ids = ids.concat(accessGroups.rows.map(accessGroup => accessGroup.value.toString()));
                rows = rows.concat(accessGroups.rows.map(row => ({ id: row.value, name: row.label })));
                pagination = accessGroups.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(ids);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const assignAccessGroups = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        assignUnassignAccessGroups(toBeAssigned, toBeUnassigned, itemId, "area").then((response) => {
            loadSiteData();
            toast.success(translate.byKey(TranslationKeys.success));
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setBusy(false);
        });
    }

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
        return tabContentList[2].content; // default to Notes for new access area
    };

    const getDataMethod = (listParameters?: ListQuery) => {
        return getAllAccessGroups(areaType, translate, {
            ...listParameters,
            filters: appendFilter(currentItem?.siteId, 'Site', 'area.site.id', listParameters?.filters ?? [])
        })
    };

    return (
        <div className='flex h-full'>
            <TopicContextProvider dataMethod={(listQuery?: ListQuery) => getAccessAreaNamesWithPagination(areaType, listQuery)} itemId={itemId} topic={topic} selectedItem={selectedItem}>
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveAccessArea}
                        tabs={tabs}
                        itemPath={isAccessArea ? itemPath : undefined}
                        initialValues={currentItem}>
                        <AccessAreaCard />
                    </SimpleItemDetails>
                </ItemContentTabTwo>
            </TopicContextProvider>
            <AssignModalTwo
                key={`assignAccessGroupsFor${itemId}`}
                assigningTitle={translate.byKey(isAccessArea? TranslationKeys.accessGroups : TranslationKeys.floorGroups)}
                dataMethod={getDataMethod}
                getAssignedItems={getAssignedAccessGroups}
                handleSubmit={assignAccessGroups}
                isOpen={showManageModal}
                modalTitle={translate.byKey(isAccessArea ? TranslationKeys.assignAccessGroups : TranslationKeys.assignedFloorGroups)}
                onClose={() => setShowManageModal(false)}
                searchColumns={AccessGroupSearch}
                tableColumns={hideColumns(AccessGroupColumns, ["days", "startTime", "duration", "enrolments", "area"])}
                lookup={lookup} />
        </div >
    )
}

export default AccessArea;

